import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { 
  Users, 
  Music, 
  Sparkles, 
  Star, 
  Shield, 
  Search,
  Flag,
  Flame
} from 'lucide-react';
import api from '../config/api';
import HeroSection from '../components/common/HeroSection';

const TeamPage = () => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [logoUrl, setLogoUrl] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');

  // Category theme styling helper
  const getCategoryTheme = (category) => {
    switch (category?.toLowerCase()) {
      case 'shambhu garjana':
        return {
          gradient: 'from-amber-500 via-orange-500 to-yellow-600',
          badgeBg: 'bg-amber-100 text-amber-900 border-amber-300',
          ring: 'from-amber-400 via-orange-500 to-amber-600',
          subtitle: 'Heavy Traditional Dhol Ensemble',
          equipment: 'Dhol • Tasha • Ghungroo'
        };
      case 'shiv shakti':
        return {
          gradient: 'from-orange-500 via-amber-600 to-red-500',
          badgeBg: 'bg-orange-100 text-orange-900 border-orange-300',
          ring: 'from-orange-400 via-amber-500 to-red-500',
          subtitle: 'High-Tempo Rhythm & Tasha Section',
          equipment: 'High-Pitch Tasha • Percussion'
        };
      case 'shiv garjana':
        return {
          gradient: 'from-red-600 via-rose-600 to-orange-600',
          badgeBg: 'bg-red-100 text-red-900 border-red-300',
          ring: 'from-red-500 via-rose-500 to-orange-500',
          subtitle: 'Grand Festival Procession Unit',
          equipment: 'Procession Dhol • Zaanz'
        };
      case 'shakti garjana':
        return {
          gradient: 'from-amber-400 via-orange-500 to-rose-500',
          badgeBg: 'bg-amber-50 text-amber-900 border-amber-200',
          ring: 'from-amber-400 via-orange-500 to-rose-400',
          subtitle: "All-Women's Powerful Percussion Force",
          equipment: 'Dhol • Energy • Leadership'
        };
      case 'rudra dance':
        return {
          gradient: 'from-purple-600 via-indigo-600 to-violet-700',
          badgeBg: 'bg-purple-100 text-purple-900 border-purple-300',
          ring: 'from-purple-400 via-indigo-500 to-violet-500',
          subtitle: 'Sacred Energetic Fusion & Classical Expression',
          equipment: 'Traditional & Fusion Choreography'
        };
      case 'folk taal (lezim, dhwaj, zaanz)':
        return {
          gradient: 'from-emerald-500 via-teal-600 to-green-600',
          badgeBg: 'bg-emerald-100 text-emerald-900 border-emerald-300',
          ring: 'from-emerald-400 via-teal-500 to-green-500',
          subtitle: 'Folk Heritage, Flag Bearers & Cymbals',
          equipment: 'Lezim • Bhagwa Dhwaj • Zaanz'
        };
      case 'shankhnaad':
        return {
          gradient: 'from-sky-500 via-blue-600 to-indigo-600',
          badgeBg: 'bg-sky-100 text-sky-900 border-sky-300',
          ring: 'from-sky-400 via-blue-500 to-indigo-500',
          subtitle: 'Auspicious Sacred Conch Fanfare',
          equipment: 'Shankh (Conch Shells)'
        };
      case 'janta raja':
        return {
          gradient: 'from-yellow-500 via-amber-600 to-orange-600',
          badgeBg: 'bg-yellow-100 text-yellow-900 border-yellow-300',
          ring: 'from-yellow-400 via-amber-500 to-orange-500',
          subtitle: 'Grand Historical Drama & Royal Fanfare',
          equipment: 'Theatrical Arts • Cultural Heritage'
        };
      case 'management':
        return {
          gradient: 'from-slate-700 via-blue-800 to-slate-900',
          badgeBg: 'bg-slate-100 text-slate-900 border-slate-300',
          ring: 'from-slate-500 via-blue-600 to-slate-700',
          subtitle: 'Executive Leadership & Event Coordination',
          equipment: 'Strategy • Operations • Logistics'
        };
      default:
        return {
          gradient: 'from-orange-500 to-amber-600',
          badgeBg: 'bg-orange-100 text-orange-900 border-orange-300',
          ring: 'from-orange-400 to-amber-500',
          subtitle: 'Cultural Performers',
          equipment: 'Indian Cultural Arts'
        };
    }
  };

  useEffect(() => {
    fetchTeamMembers();
    fetchLogo();
  }, []);

  const fetchLogo = async () => {
    try {
      const response = await api.get('/about-page');
      if (response.ok) {
        const data = await response.json();
        if (data.logo) {
          setLogoUrl(data.logo);
        }
      }
    } catch (error) {
      console.error('Error fetching logo:', error);
    }
  };

  const parseMemberCategories = (member) => {
    if (!member) return ['Shambhu Garjana'];
    let cats = member.categories;
    if (typeof cats === 'string') {
      try {
        cats = JSON.parse(cats);
      } catch (e) {
        if (cats.includes(',')) {
          cats = cats.split(',').map(s => s.trim()).filter(Boolean);
        } else if (cats.trim()) {
          cats = [cats.trim()];
        }
      }
    }
    if (!Array.isArray(cats) || cats.length === 0) {
      cats = member.category ? [member.category] : ['Shambhu Garjana'];
    }
    return cats;
  };

  const fetchTeamMembers = async () => {
    try {
      setLoading(true);
      const response = await api.get('team');
      if (response.ok) {
        const data = await response.json();
        const parsed = Array.isArray(data) ? data.map(m => ({
          ...m,
          categories: parseMemberCategories(m),
          category: parseMemberCategories(m)[0] || m.category || 'Shambhu Garjana'
        })) : [];
        setMembers(parsed);
      } else {
        console.error('Failed to fetch team members');
        setMembers([]);
      }
    } catch (error) {
      console.error('Error fetching team members:', error);
      setMembers([]);
    } finally {
      setLoading(false);
    }
  };

  // Default team categories to render in order if present
  const defaultCategories = [
    'Shambhu Garjana',
    'Shiv Shakti',
    'Shiv Garjana',
    'Shakti Garjana',
    'Rudra Dance',
    'ShankhNaad',
    'Folk Taal (Lezim, Dhwaj, Zaanz)',
    'Janta Raja',
    'Management'
  ];

  // Gather all unique categories present in members + default ones
  const allCategories = [
    ...new Set([
      ...defaultCategories,
      ...members.flatMap(m => Array.isArray(m.categories) && m.categories.length > 0 ? m.categories : [m.category]).filter(Boolean)
    ])
  ];

  // Category Icon Map
  const getCategoryIcon = (category) => {
    switch (category?.toLowerCase()) {
      case 'shambhu garjana':
        return <Music className="w-6 h-6 text-white" />;
      case 'shiv shakti':
        return <Flame className="w-6 h-6 text-white" />;
      case 'shiv garjana':
        return <Flame className="w-6 h-6 text-white" />;
      case 'shakti garjana':
        return <Flame className="w-6 h-6 text-white" />;
      case 'rudra dance':
        return <Sparkles className="w-6 h-6 text-white" />;
      case 'folk taal (lezim, dhwaj, zaanz)':
        return <Flag className="w-6 h-6 text-white" />;
      case 'janta raja':
        return <Star className="w-6 h-6 text-white" />;
      case 'management':
        return <Shield className="w-6 h-6 text-white" />;
      default:
        return <Users className="w-6 h-6 text-white" />;
    }
  };

  // Filter members based on search
  const filteredMembers = members.filter(member => {
    return !searchQuery ||
           member.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
           member.role?.toLowerCase().includes(searchQuery.toLowerCase());
  });

  return (
    <>
      <Helmet>
        <title>Cultural Teams & Performers - Beats of Washington</title>
        <meta 
          name="description" 
          content="Meet the Shambhu Garjana, Shiv Shakti, Shiv Garjana, Shakti Garjana, Rudra Dance, ShankhNaad, Folk Taal, and Janta Raja teams of Beats of Washington." 
        />
      </Helmet>

      {/* Hero Section */}
      <HeroSection
        title={
          <>
            <span>Our Performers</span>
            <br />
            <span>& Cultural</span>
            <br />
            <span>Teams</span>
          </>
        }
        description="Celebrate the rhythm, energy, and passion of our cultural teams."
        logoUrl={logoUrl}
        showLogo={true}
        floatingElements={[
          { icon: Music, position: 'top-12 left-16', animation: 'animate-bounce' },
          { icon: Sparkles, position: 'top-24 right-24', animation: 'animate-pulse' },
          { icon: Shield, position: 'bottom-20 left-1/4', animation: 'animate-spin-slow' },
          { icon: Star, position: 'bottom-16 right-1/3', animation: 'animate-bounce' }
        ]}
      />

      <main className="bg-gradient-to-b from-gray-50 via-white to-orange-50/20 min-h-screen py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto space-y-12">

          {/* Section Introduction */}
          <div className="text-center max-w-3xl mx-auto space-y-3">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-orange-100/80 text-orange-800 rounded-full text-xs font-extrabold uppercase tracking-wider">
              <Sparkles className="w-4 h-4 text-orange-600" />
              <span>Cultural Showcase</span>
            </div>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-gray-900 tracking-tight">
              Beats of Washington Teams
            </h2>
          </div>

          {/* Top Search & Filter Bar */}
          <div className="space-y-4 max-w-4xl mx-auto">
            <div className="bg-white rounded-3xl p-4 shadow-xl border border-gray-100 flex justify-center items-center max-w-md mx-auto">
              <div className="relative w-full">
                <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search performer name or role..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-2xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:bg-white transition-all text-center"
                />
              </div>
            </div>

            {/* Category Quick Filter Pills */}
            <div className="flex flex-wrap items-center justify-center gap-2 pt-2 px-2">
              <button
                type="button"
                onClick={() => setSelectedCategory('All')}
                className={`px-4 py-2 rounded-full text-xs sm:text-sm font-bold transition-all duration-300 ${
                  selectedCategory === 'All'
                    ? 'bg-gradient-to-r from-orange-500 to-amber-500 text-white shadow-lg shadow-orange-500/30 scale-105'
                    : 'bg-white text-gray-700 hover:bg-orange-50 border border-gray-200'
                }`}
              >
                All Ensembles ({members.length})
              </button>
              {allCategories.map((cat) => {
                const count = members.filter(m => {
                  const memberCats = Array.isArray(m.categories) && m.categories.length > 0 ? m.categories : [m.category];
                  return memberCats.includes(cat);
                }).length;
                if (count === 0) return null;

                return (
                  <button
                    key={cat}
                    type="button"
                    onClick={() => setSelectedCategory(cat)}
                    className={`px-3.5 py-1.5 rounded-full text-xs sm:text-sm font-semibold transition-all duration-300 ${
                      selectedCategory === cat
                        ? 'bg-gradient-to-r from-orange-500 to-amber-500 text-white shadow-md scale-105'
                        : 'bg-white text-gray-700 hover:bg-orange-50 border border-gray-200'
                    }`}
                  >
                    {cat} ({count})
                  </button>
                );
              })}
            </div>
          </div>

          {/* Loading State */}
          {loading ? (
            <div className="py-20 text-center">
              <div className="w-12 h-12 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-gray-500 font-semibold text-base">Loading team members...</p>
            </div>
          ) : filteredMembers.length === 0 ? (
            <div className="bg-white rounded-3xl p-16 text-center shadow-lg border border-gray-100 max-w-md mx-auto">
              <Users className="w-16 h-16 text-orange-300 mx-auto mb-4 animate-pulse" />
              <h3 className="text-xl font-bold text-gray-900">No Team Members Found</h3>
              <p className="text-gray-500 text-sm mt-2">
                No performers match your search or selected section filter.
              </p>
            </div>
          ) : (
            /* Categorized Team Sections */
            <div className="space-y-16">
              {allCategories
                .filter(cat => selectedCategory === 'All' || selectedCategory === cat)
                .map((category) => {
                const categoryMembers = filteredMembers.filter(m => {
                  const memberCats = Array.isArray(m.categories) && m.categories.length > 0 ? m.categories : [m.category];
                  return memberCats.includes(category);
                });
                if (categoryMembers.length === 0) return null;
                const theme = getCategoryTheme(category);

                return (
                  <section key={category} className="space-y-8 card-glass rounded-3xl p-6 sm:p-8 md:p-10 border border-white/80 shadow-xl">
                    {/* Team Header Banner with Rich Theme Badge */}
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b-2 border-orange-100/80 pb-5">
                      <div className="flex items-center gap-3.5">
                        <div className={`p-3.5 rounded-2xl shadow-md bg-gradient-to-br ${theme.gradient} text-white`}>
                          {getCategoryIcon(category)}
                        </div>
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="text-2xl sm:text-3xl font-black text-gray-900 tracking-tight">
                              {category}
                            </h3>
                            <span className={`text-xs font-bold px-2.5 py-1 rounded-full border ${theme.badgeBg}`}>
                              {categoryMembers.length} {categoryMembers.length === 1 ? 'Performer' : 'Performers'}
                            </span>
                          </div>
                          <p className="text-xs sm:text-sm font-medium text-gray-500 mt-0.5">
                            {theme.subtitle}
                          </p>
                        </div>
                      </div>

                      {/* Equipment / Instrument Pill */}
                      <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-xl bg-orange-50/80 border border-orange-200/60 text-xs font-semibold text-orange-900 self-start sm:self-auto">
                        <Music className="w-3.5 h-3.5 text-orange-600" />
                        <span>{theme.equipment}</span>
                      </div>
                    </div>

                    {/* Circular Avatar Grid for this Team */}
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-6 sm:gap-8">
                      {categoryMembers.map((member) => (
                        <div
                          key={member.id}
                          className="group bg-white rounded-3xl p-5 border border-gray-100 shadow-md hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 flex flex-col items-center text-center relative overflow-hidden"
                        >
                          {/* Top accent line matching team theme */}
                          <div className={`absolute top-0 inset-x-0 h-1.5 bg-gradient-to-r ${theme.gradient} opacity-0 group-hover:opacity-100 transition-opacity`} />

                          {/* Circle Avatar with Colored Glow Ring */}
                          <div className={`w-28 h-28 sm:w-32 sm:h-32 rounded-full p-1.5 bg-gradient-to-tr ${theme.ring} shadow-lg group-hover:shadow-orange-500/30 group-hover:scale-105 transition-all duration-300 mb-4 relative flex-shrink-0`}>
                            {member.photo ? (
                              <img
                                src={member.photo}
                                alt={member.name}
                                className="w-full h-full rounded-full object-cover bg-white"
                              />
                            ) : (
                              <div className={`w-full h-full rounded-full bg-gradient-to-br ${theme.gradient} flex items-center justify-center text-white font-black text-3xl`}>
                                {member.name ? member.name.charAt(0) : 'T'}
                              </div>
                            )}
                          </div>

                          {/* Member Name (Full Name & Surname) */}
                          <h4 className="font-black text-gray-900 text-sm sm:text-base md:text-lg group-hover:text-orange-600 transition-colors leading-snug break-words max-w-full">
                            {member.name}
                          </h4>

                          {/* Optional Role if provided */}
                          {member.role && (
                            <p className="text-xs font-bold text-gray-500 mt-1 leading-tight break-words max-w-full">
                              {member.role}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  </section>
                );
              })}
            </div>
          )}
        </div>
      </main>
    </>
  );
};

export default TeamPage;
